import React, {useCallback, useState} from 'react';
import {ScrollView, Switch, View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react';

import Text from '../../../components/themed/Text';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import apiService from '../../../transport/apiService';

const AccountCommunicationsPreferencesScreen = observer(
  ({
    navigation,
    route,
  }: RouteNavigatorScreenProps<AppRoute.ACCOUNT_COMMUNICATIONS_PREFERENCES>) => {
    const styles = useStyles();

    const [emailEnabled, setEmailEnabled] = useState(true);
    const [smsEnabled, setSmsEnabled] = useState(true);
    const [loading, setLoading] = useState(true);

    useFocusEffect(
      useCallback(() => {
        let isActive = true;

        const fetchPreferences = async () => {
          try {
            setLoading(true);
            const response = await apiService.getCommunicationsPreferences();
            if (response?.data && isActive) {
              setEmailEnabled(!response.data.Email_Opt_Out);
              setSmsEnabled(!response.data.SMS_Opt_Out);
            }
          } catch (error) {
            console.error('Failed to fetch communication preferences', error);
          } finally {
            if (isActive) {
              setLoading(false);
            }
          }
        };

        fetchPreferences();

        // Cleanup function to set isActive to false when the screen is unfocused
        return () => {
          isActive = false;
        };
      }, []),
    );

    const toggleEmailSwitch = async () => {
      const newEmailEnabled = !emailEnabled;
      setEmailEnabled(newEmailEnabled);

      try {
        await apiService.updateCommunicationsPreferences({
          emailOptOut: !newEmailEnabled,
          smsOptOut: !smsEnabled,
        });
      } catch (error) {
        console.error('Failed to update email preference', error);
        setEmailEnabled(emailEnabled);
      }
    };

    const toggleSmsSwitch = async () => {
      const newSmsEnabled = !smsEnabled;
      setSmsEnabled(newSmsEnabled);

      try {
        await apiService.updateCommunicationsPreferences({
          emailOptOut: !emailEnabled,
          smsOptOut: !newSmsEnabled,
        });
      } catch (error) {
        console.error('Failed to update SMS preference', error);
        setSmsEnabled(smsEnabled);
      }
    };

    if (loading) {
      return (
        <ContentView>
          <Text>Loading preferences...</Text>
        </ContentView>
      );
    }

    return (
      <ScrollView>
        <ContentView testID="account-communications-preferences-screen">
          <View style={styles.headingContainer}>
            <Text noMargin variant="heading1">
              MARKETING
            </Text>
          </View>

          {/* Email Preference Row */}
          <View style={styles.preferenceContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.preferenceTitle}>Email</Text>
              <Text style={styles.preferenceDescription}>
                This will opt you out of any and all email communications
              </Text>
            </View>
            <Switch
              trackColor={{false: '#767577', true: '#0054A6'}}
              // thumbColor={emailEnabled ? '#f5dd4b' : '#f5dd4b'}

              ios_backgroundColor="#3e3e3e"
              onValueChange={toggleEmailSwitch}
              value={emailEnabled}
            />
          </View>

          {/* SMS Preference Row */}
          <View style={styles.preferenceContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.preferenceTitle}>SMS</Text>
              <Text style={styles.preferenceDescription}>
                This will opt you out of any and all SMS communications
              </Text>
            </View>
            <Switch
              trackColor={{false: '#767577', true: '#0054A6'}}
              // thumbColor={smsEnabled ? '#f5dd4b' : '#f5dd4b'}

              ios_backgroundColor="#3e3e3e"
              onValueChange={toggleSmsSwitch}
              value={smsEnabled}
            />
          </View>
        </ContentView>
      </ScrollView>
    );
  },
);

const useStyles = makeStyles(() => ({
  headingContainer: {
    marginBottom: 16,
  },
  preferenceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  textContainer: {
    flex: 1,
    marginRight: 12,
  },
  preferenceTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  preferenceDescription: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'left',
    color: 'grey',
  },
}));

export default AccountCommunicationsPreferencesScreen;
